import {DataFrame} from 'data-frames/DataFrameTypes';
import {
  ColumnValidationMap,
  ColumnsMapping,
  numericAccessor,
  stringAccessor,
} from 'data-frames/util/AccessorUtils';
import {DataFrameValueAccessor, createAccessors} from 'data-frames/util/createAccessors';
import {ColumnTypeValidationFlag} from 'data-frames/util/validateColumnType';
import {LeafType, NUMERICAL_LEAF_TYPES} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {JSONValue} from 'util/Types';

type RegressionModelVariablesRequiredColumns =
  | 'model_type'
  | 'parameter'
  | 'value'
  | 'confidence_interval_lower_bound'
  | 'confidence_interval_upper_bound'
  | 'error';
type RegressionModelVariablesOptionalColumns = 'partition' | 'p_value';

type RegressionModelVariablesColumns =
  | RegressionModelVariablesRequiredColumns
  | RegressionModelVariablesOptionalColumns;

export const REGRESSION_MODEL_VARIABLES_VALIDATION_MAP: ColumnValidationMap<RegressionModelVariablesColumns> =
  {
    model_type: {allowedLeafTypes: [LeafType.TEXT]},
    parameter: {allowedLeafTypes: [LeafType.TEXT]},
    value: {allowedLeafTypes: [...NUMERICAL_LEAF_TYPES], nullableType: ColumnTypeValidationFlag.ALLOWED},
    confidence_interval_lower_bound: {
      allowedLeafTypes: [...NUMERICAL_LEAF_TYPES],
      nullableType: ColumnTypeValidationFlag.ALLOWED,
    },
    confidence_interval_upper_bound: {
      allowedLeafTypes: [...NUMERICAL_LEAF_TYPES],
      nullableType: ColumnTypeValidationFlag.ALLOWED,
    },
    error: {allowedLeafTypes: [LeafType.TEXT], nullableType: ColumnTypeValidationFlag.ALLOWED},
    partition: {},
    p_value: {
      allowedLeafTypes: [...NUMERICAL_LEAF_TYPES],
      nullableType: ColumnTypeValidationFlag.ALLOWED,
    },
  };

export type RegressionModelVariablesColumnsMapping = ColumnsMapping<
  RegressionModelVariablesRequiredColumns,
  RegressionModelVariablesOptionalColumns
>;

export interface RegressionModelVariablesAccessors {
  modelTypeAccessor: DataFrameValueAccessor<string>;
  parameterAccessor: DataFrameValueAccessor<string>;
  valueAccessor: DataFrameValueAccessor<number | null>;
  confidenceIntervalLowerBoundAccessor: DataFrameValueAccessor<number | null>;
  confidenceIntervalUpperBoundAccessor: DataFrameValueAccessor<number | null>;
  errorAccessor: DataFrameValueAccessor<string | null>;
  partitionAccessor?: DataFrameValueAccessor<JSONValue>;
  pValueAccessor?: DataFrameValueAccessor<number | null>;
}

export function getRegressionModelVariablesAccessors(
  dataFrame: DataFrame
): RegressionModelVariablesAccessors {
  const {accessors, validatedLeafTypes} = createAccessors(
    dataFrame,
    {
      model_type: 'model_type',
      parameter: 'parameter',
      value: 'value',
      error: 'error',
      confidence_interval_lower_bound: 'confidence_interval_lower_bound',
      confidence_interval_upper_bound: 'confidence_interval_upper_bound',
      ...(dataFrame.columns.find((column) => column.systemName === 'partition') != null
        ? {partition: 'partition'}
        : {}),
      ...(dataFrame.columns.find((column) => column.systemName === 'p_value') != null
        ? {p_value: 'p_value'}
        : {}),
    },
    REGRESSION_MODEL_VARIABLES_VALIDATION_MAP
  );

  return {
    modelTypeAccessor: stringAccessor(accessors.model_type),
    parameterAccessor: stringAccessor(accessors.parameter),
    valueAccessor: numericAccessor(accessors.value, validatedLeafTypes.value),
    confidenceIntervalLowerBoundAccessor: numericAccessor(
      accessors.confidence_interval_lower_bound,
      validatedLeafTypes.confidence_interval_lower_bound
    ),
    confidenceIntervalUpperBoundAccessor: numericAccessor(
      accessors.confidence_interval_upper_bound,
      validatedLeafTypes.confidence_interval_upper_bound
    ),
    errorAccessor: stringAccessor(accessors.error),
    partitionAccessor: accessors.partition,
    pValueAccessor:
      accessors.p_value != null && validatedLeafTypes.p_value != null
        ? numericAccessor(accessors.p_value, validatedLeafTypes.p_value)
        : undefined,
  };
}
